import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { Observable, Subscription, filter } from "rxjs";
import { AppState } from "src/app/store/app.reducer";
import * as DeviceActions from 'src/app/store/device/device.actions';
import {
	selectCurrentDevice,
	selectCurrentDeviceModel,
	selectCurrentDeviceStatus,
	selectFirmwareUpdate,
	selectfwUpdateStart,
} from "src/app/store/device/device.selector";
import { selectCurrentSite } from "src/app/store/site/site.selector";
import { environment } from "src/environments/environment";
import {
	AutomationFirmwareRow,
	FirmwareUpdate,
	PostFirmwareUpdate,
	connectedAutomationsForGateway,
	mapAutomationsToFirmwareUpdates,
	splitGatewayFirmwareUpdates,
} from "src/app/model/firmware-update";
import { DeviceStatus, isDeviceUnavailable } from "src/app/model/device-status";
import { CLOUD_FW_UPGRADE_STATE } from "src/app/conf/firmware-upgrade";
import { StandardPageLayout } from "src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib";
import { Device } from "src/app/model/device";
import { DeviceModel } from "src/app/model/device-model";
import { Site } from "src/app/model/site";
import { EDeviceModel } from "src/app/conf/device-models";
import { CommonCustomConfirmDialogComponent } from "src/app/modules/shared/components/common-custom-confirm-dialog/common-custom-confirm.dialog.component";

@Component({
	templateUrl: './update-firmware.dialog.component.html',
	providers: [DialogService],
	styles: [`
		:host ::ng-deep .p-tabview-nav > li {
			display: flex;
		}
		:host ::ng-deep .p-tabview-nav > li > .p-tabview-nav-link {
			flex: 1 1 auto;
			align-items: center;
		}
		.fw-update-empty {
			color: #65738D;
		}
	`]
})

export class UpdateFirmwareDialogComponent implements OnInit, OnDestroy {
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;
	firmwareUpdate$: Observable<FirmwareUpdate[] | undefined>;
	firmwareUpdateSub: Subscription | undefined;
	currentDeviceSub: Subscription | undefined;
	currentDeviceModelSub: Subscription | undefined;
	currentSiteSub: Subscription | undefined;

	deviceStatus: DeviceStatus;
	mtmaFirmwareUpdates: FirmwareUpdate[] = [];
	currentDevice: Device;
	currentDeviceModel: DeviceModel;
	currentSite: Site;
	automationFwRows: AutomationFirmwareRow[] = [];

	fwUpdateStart = false;
	fwUpdateStart$: Observable<boolean | undefined>;
	fwUpdateStartSub: Subscription | undefined;

	screenWidth: number;
	nestedRef: DynamicDialogRef | undefined;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private dialogRef: DynamicDialogRef,
		public config: DynamicDialogConfig,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.firmwareUpdate$ = this.store.select(selectFirmwareUpdate);
		this.fwUpdateStart$ = this.store.select(selectfwUpdateStart);
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.onResize();
	}

	subscribeAll(): void {
		this.firmwareUpdateSub = this.firmwareUpdate$.subscribe(mtmaFirmwareUpdates => {
			this.mtmaFirmwareUpdates = (mtmaFirmwareUpdates || []).filter(fir => fir.AvailableFWVersion);
			this.refreshAutomationFwRows();
		});
		this.deviceStatusSub = this.deviceStatus$.pipe(filter(d => !!d))
			.subscribe(currentDeviceStatus => {
				this.deviceStatus = currentDeviceStatus;
			});
		this.fwUpdateStartSub = this.fwUpdateStart$.subscribe(fwUpdateStart => {
			this.fwUpdateStart = fwUpdateStart;
		});
		this.currentDeviceSub = this.store.select(selectCurrentDevice).subscribe(device => {
			this.currentDevice = device;
			this.refreshAutomationFwRows();
		});
		this.currentDeviceModelSub = this.store.select(selectCurrentDeviceModel).subscribe(deviceModel => {
			this.currentDeviceModel = deviceModel;
		});
		this.currentSiteSub = this.store.select(selectCurrentSite).subscribe(site => {
			this.currentSite = site;
			this.refreshAutomationFwRows();
		});
	}

	get showGroupedUpdates(): boolean {
		return this.currentDevice?.ModelId === EDeviceModel.GWETH;
	}

	get gatewayFirmwareUpdates(): FirmwareUpdate[] {
		return this.splitAvailableFirmware().gateway;
	}

	get hasAutomationFirmwareUpdates(): boolean {
		return this.automationFwRows.some(row => !!row.update);
	}

	get automationFirmwareUpdates(): FirmwareUpdate[] {
		return this.splitAvailableFirmware().automations;
	}

	private splitAvailableFirmware(): { gateway: FirmwareUpdate[]; automations: FirmwareUpdate[] } {
		return splitGatewayFirmwareUpdates(
			this.mtmaFirmwareUpdates,
			[],
			[
				this.currentDevice?.Name,
				this.currentDeviceModel?.ProductName,
				this.currentDeviceModel?.ProductBoardName,
				this.currentDeviceModel?.BoardName,
				'GWETH',
			]
		);
	}

	closeDialog(): void {
		this.dialogRef.close();
	}

	ngOnDestroy(): void {
		this.deviceStatusSub?.unsubscribe();
		this.firmwareUpdateSub?.unsubscribe();
		this.fwUpdateStartSub?.unsubscribe();
		this.currentDeviceSub?.unsubscribe();
		this.currentDeviceModelSub?.unsubscribe();
		this.currentSiteSub?.unsubscribe();
		this.nestedRef?.close();
	}

	firmwareUpdate(device: string): void {
		let test = false;
		if (environment.apiUrl !== 'https://app.cameconnect.net/api') {
			test = true;
		}
		const firmware = new PostFirmwareUpdate();
		firmware.Component = device;
		this.store.dispatch(DeviceActions.postFirmwareUpdate({ firmware, test }));
		this.closeDialog();
	}

	confirmFirmwareUpdate(firmware: FirmwareUpdate, messageKey: string): void {
		this.nestedRef = this.dialogService.open(CommonCustomConfirmDialogComponent, {
			header: this.translate.instant('UPDATE_FIRWARE'),
			contentStyle: { overflow: 'auto' },
			data: {
				icon: 'cm-alert',
				row1: messageKey,
				row1Params: {
					name: firmware.Description,
					from: firmware.DeviceFWVersion,
					to: firmware.AvailableFWVersion,
				},
				labelAcceptButton: 'CONFIRM',
				labelRejectButton: 'CANCEL',
				classAcceptButton: 'p-button-warning',
			},
		});
		this.nestedRef.onClose.subscribe((accepted: boolean) => {
			if (accepted) {
				this.firmwareUpdate(firmware.Description);
			}
		});
	}

	offline(): boolean {
		return !this.deviceStatus.Online;
	}

	isDeviceUnavailable(): boolean {
		return isDeviceUnavailable(this.deviceStatus);
	}

	onFirmwareUpdate(): boolean {
		return this.deviceStatus.FwUpdate === CLOUD_FW_UPGRADE_STATE.CLOUD_FW_UPGRADE_STATE_STARTED ||
			this.deviceStatus.FwUpdate === CLOUD_FW_UPGRADE_STATE.CLOUD_FW_UPGRADE_STATE_UPDATING;
	}

	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile() {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	private refreshAutomationFwRows(): void {
		const connected = connectedAutomationsForGateway(
			this.currentDevice?.Id,
			this.currentSite?.Devices || []
		);
		if (connected.length > 0) {
			this.automationFwRows = mapAutomationsToFirmwareUpdates(connected, this.mtmaFirmwareUpdates);
			return;
		}
		this.automationFwRows = this.automationFirmwareUpdates.map(update => ({
			device: { Id: 0, Name: update.Description },
			update,
		}));
	}
}
